import { Tick02Icon } from '@hugeicons/react'
import clsx from 'clsx'
import { useFormContext, useWatch } from 'react-hook-form'

import { isRetiredTag, mergeDeclaredIntoVocabulary, toggleTagId } from '../model/diff'

import type { NetworkingTag } from '../model/types'

type NetworkingTagPickerProps = {
  /** Vocabulário ativo do evento. */
  vocabulary: NetworkingTag[]
  /** Tags já declaradas, incluindo as que o produtor desativou depois. */
  declaredTags: NetworkingTag[]
}

/**
 * Chips de seleção múltipla.
 *
 * Escolha deliberada sobre combobox: o app é usado com o polegar, em movimento, e
 * o vocabulário é curto e curado pelo produtor.
 *
 * Não reusa `shared/ui/Layout/page/FilterChips` porque aquele primitivo é de
 * seleção única (`value` + `onChange(id)`, `aria-pressed` em um só) e é uma linha
 * com `overflow-x`, não uma grade. O que se reaproveita dele é a regra de alvo de
 * toque do `design-system/ACCESSIBILITY.md`.
 *
 * O alvo usa `min-h-[var(--target-min)]` (44×44) em vez do pseudo-elemento do
 * FilterChips: aqui os chips quebram linha, e uma área expandida verticalmente se
 * sobreporia à linha vizinha — toque na borda ativaria o chip errado, que é pior
 * que alvo pequeno. Com a caixa visual igual à tocável, o problema não existe.
 */
export const NetworkingTagPicker = ({
  vocabulary,
  declaredTags
}: NetworkingTagPickerProps) => {
  const { control, setValue } = useFormContext()
  const selected: number[] = useWatch({ control, name: 'networking_tag_ids' }) ?? []

  const options = mergeDeclaredIntoVocabulary(vocabulary, declaredTags)

  // RF-15: sem vocabulário e sem nada declarado, não renderiza — em vez de exibir
  // uma lista vazia.
  if (options.length === 0) return null

  const handleToggle = (id: number) =>
    setValue('networking_tag_ids', toggleTagId(selected, id), {
      shouldDirty: true
    })

  return (
    <fieldset className="m-0 border-0 p-0">
      <legend className="p-0 text-base font-semibold text-gray-900">
        Seus interesses
      </legend>
      <p className="mb-3 mt-1 text-sm text-gray-700">
        Escolha o que você busca ou oferece neste evento. Usamos isso para sugerir
        pessoas, empresas e conteúdos com afinidade.
      </p>

      <div className="flex flex-wrap gap-2" role="group">
        {options.map(tag => {
          const isSelected = selected.includes(tag.id)
          // Tag declarada que o produtor desativou depois: continua removível,
          // mas é marcada para o participante entender por que ela não aparece
          // mais como opção nova.
          const retired = isRetiredTag(tag, vocabulary)

          return (
            <button
              key={tag.id}
              type="button"
              onClick={() => handleToggle(tag.id)}
              aria-pressed={isSelected}
              className={clsx(
                'inline-flex items-center gap-2 rounded-[10px] border px-3 text-sm font-semibold transition-colors',
                'min-h-[var(--target-min)]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
                isSelected
                  ? 'border-transparent bg-brand text-[var(--color-brand-inverse)] shadow-[0_2px_6px_color-mix(in_oklab,var(--color-brand)_30%,transparent)]'
                  // `border-border` (#f4f4f4) sobre o card branco dava ~1:1 de
                  // contraste: o chip não se lia como controle. `gray-600` é o
                  // degrau que passa nos 3:1 de ACCESSIBILITY.md §1.4.11.
                  : 'border-gray-600 bg-white text-gray-900 hover:bg-gray-100',
                retired && 'opacity-70'
              )}
            >
              {/* Caixa sempre presente: em repouso ela é o que anuncia que o
                  chip é selecionável — sem marcador, o participante só descobre
                  tocando. Marcada, o check atende §1.5, que proíbe comunicar
                  estado apenas por cor. */}
              <span
                aria-hidden
                className={clsx(
                  'grid h-[18px] w-[18px] shrink-0 place-content-center rounded-[4px] border transition-colors',
                  isSelected
                    ? 'border-white bg-white text-brand'
                    : 'border-gray-600 bg-white'
                )}
              >
                {isSelected && <Tick02Icon variant="stroke" size={14} />}
              </span>
              {tag.name}
              {retired && (
                <span className="text-sm font-normal">(descontinuado)</span>
              )}
            </button>
          )
        })}
      </div>
    </fieldset>
  )
}
